import { isEmpty, isNil } from 'lodash'

import { BOOLEAN_TYPE_KEY, getRowTypeKey, normalizeFieldKey, PICK_LIST_TYPE_KEY } from './index'

// A field link is one bit flow edge held in form state, keyed by field NAME
// because that is what the wire shape uses (source_field_name/target_field_name)
// and what a row identifies itself by. Renaming a field therefore has to rewrite
// both ends — see renameFieldInLinks.
//
// This module is all pure functions over (promptFields, links) so the rules that
// matter — what can gate what, which operators a source allows, what the payload
// looks like — are testable without rendering the editor.

// Operators the summary service accepts on an edge (BitEdgeOperator), with the
// plain-English wording the editor shows. `numeric` operators are rejected at
// save on a boolean or taxonomy-bound source, so those types don't offer them.
export const EDGE_OPERATOR = {
  eq: 'eq',
  neq: 'neq',
  in: 'in',
  gt: 'gt',
  gte: 'gte',
  lt: 'lt',
  lte: 'lte',
  exists: 'exists',
  not_exists: 'not_exists',
}

// A Yes/No gate stores the string 'true'/'false' — what the condition editor
// writes and what the service compares against. Shared with the branch picker so
// the two can't drift into storing one thing and offering another.
export const BOOLEAN_OPERAND_OPTIONS = [
  { value: 'true', label: 'Yes' },
  { value: 'false', label: 'No' },
]

const PRESENCE_OPERATORS = [
  { value: EDGE_OPERATOR.exists, label: 'has any value' },
  { value: EDGE_OPERATOR.not_exists, label: 'has no value' },
]

const EQUALITY_OPERATORS = [
  { value: EDGE_OPERATOR.eq, label: 'is' },
  { value: EDGE_OPERATOR.neq, label: 'is not' },
]

const NUMERIC_OPERATORS = [
  { value: EDGE_OPERATOR.gt, label: 'is greater than' },
  { value: EDGE_OPERATOR.gte, label: 'is at least' },
  { value: EDGE_OPERATOR.lt, label: 'is less than' },
  { value: EDGE_OPERATOR.lte, label: 'is at most' },
]

const IN_OPERATOR = { value: EDGE_OPERATOR.in, label: 'is one of' }

// Operators offered for a source of each row type. A numeric operator on a
// boolean or taxonomy-bound field would gate every call to false, so the picker
// never offers it rather than letting the save fail.
const OPERATORS_BY_TYPE = {
  [PICK_LIST_TYPE_KEY]: [...EQUALITY_OPERATORS, IN_OPERATOR, ...PRESENCE_OPERATORS],
  string: [...EQUALITY_OPERATORS, IN_OPERATOR, ...PRESENCE_OPERATORS],
  [BOOLEAN_TYPE_KEY]: [...EQUALITY_OPERATORS, ...PRESENCE_OPERATORS],
  integer: [...EQUALITY_OPERATORS, ...NUMERIC_OPERATORS, ...PRESENCE_OPERATORS],
}

export const getOperatorsForSource = (sourceField) =>
  OPERATORS_BY_TYPE[getRowTypeKey(sourceField)] ?? OPERATORS_BY_TYPE.string

export const getOperatorLabel = (sourceField, operator) => {
  const match = getOperatorsForSource(sourceField).find((option) => option.value === operator)
  return match?.label ?? operator
}

// exists/not_exists test presence and the service rejects a compare_value on
// them; everything else requires one.
export const operatorNeedsValue = (operator) =>
  operator !== EDGE_OPERATOR.exists && operator !== EDGE_OPERATOR.not_exists

// `in` stores its operand as a JSON array. The service also accepts a
// comma-separated list, so read both and always write the array.
export const parseOperandList = (compareValue) => {
  if (isNil(compareValue)) {
    return []
  }
  try {
    const parsed = JSON.parse(compareValue)
    if (Array.isArray(parsed)) {
      return parsed
    }
  } catch (err) {
    // Not JSON — fall through to the comma-separated reading.
  }
  return String(compareValue)
    .split(',')
    .map((part) => part.trim())
    .filter((part) => !isEmpty(part))
}

export const serializeOperandList = (values) => JSON.stringify(values)

// A field can never depend on one that runs after it: agent-visible fields are
// processed first at call end on a latency budget, so they cannot wait on a
// Processed Later field. The reverse direction is fine.
export const canDependOn = (targetField, sourceField) =>
  !(targetField?.show_to_user === true && sourceField?.show_to_user !== true)

const linksBySource = (links) => {
  const bySource = {}
  links.forEach((link) => {
    const source = normalizeFieldKey(link.source)
    bySource[source] = bySource[source] ?? []
    bySource[source].push(normalizeFieldKey(link.target))
  })
  return bySource
}

// Whether `toKey` is reachable downstream of `fromKey`. Offering a source the
// target can already reach would make a cycle, which the service rejects.
export const reachesField = (links, fromKey, toKey) => {
  const bySource = linksBySource(links)
  const target = normalizeFieldKey(toKey)
  const stack = [normalizeFieldKey(fromKey)]
  const seen = []

  while (!isEmpty(stack)) {
    const node = stack.pop()
    if (node === target) {
      return true
    }
    if (!seen.includes(node)) {
      seen.push(node)
      stack.push(...(bySource[node] ?? []))
    }
  }
  return false
}

// The fields a row may be gated on: not itself, not something that runs later,
// and not anything already downstream of it.
export const getEligibleSources = (promptFields, links, targetField) =>
  promptFields.filter(
    (candidate) =>
      !isEmpty((candidate.key ?? '').trim()) &&
      normalizeFieldKey(candidate.key) !== normalizeFieldKey(targetField.key) &&
      canDependOn(targetField, candidate) &&
      !reachesField(links, targetField.key, candidate.key)
  )

// A gate on an option-backed field pins one specific option by category uuid.
// When those options are the org default's, the defaults editor can delete that
// option out from under the gate — and it has no way to know which playbooks
// gate on what, so nothing there would warn. The option lock that protects a
// playbook's own options (see getGatedOperands) can't reach across that
// boundary either. So a field whose taxonomy is inherited is offered but not
// selectable; forking its options into this configuration makes it gateable.
export const INHERITED_OPTIONS_GATE_REASON =
  'Options come from your organization defaults - break them from the defaults to gate on this field'

export const getGateBlockedReason = (sourceField, inheritedTaxonomyKeys) => {
  if (isNil(sourceField) || getRowTypeKey(sourceField) !== PICK_LIST_TYPE_KEY) {
    return null
  }
  return inheritedTaxonomyKeys?.has(normalizeFieldKey(sourceField.key)) === true
    ? INHERITED_OPTIONS_GATE_REASON
    : null
}

export const getLinksForTarget = (links, fieldKey) =>
  links.filter((link) => normalizeFieldKey(link.target) === normalizeFieldKey(fieldKey))

export const getLinksForSource = (links, fieldKey) =>
  links.filter((link) => normalizeFieldKey(link.source) === normalizeFieldKey(fieldKey))

export const findPromptField = (promptFields, fieldKey) =>
  promptFields.find((field) => normalizeFieldKey(field.key) === normalizeFieldKey(fieldKey))

// A gate on a selection field stores the option's stable category uuid, not its
// name, so renaming an option can't break the gate. Chips still have to read as
// names, so the org field list (which carries both) is indexed once.
export const buildOptionNameIndex = (orgFieldNames = []) => {
  const index = {}
  orgFieldNames.forEach((field) => {
    ;(field.options ?? []).forEach((option) => {
      index[option.category_uuid] = option.name
    })
  })
  return index
}

// A field whose taxonomy doesn't exist yet has no category uuid to gate on, so
// a condition against it is authored by option NAME and resolved once the save
// mints the taxonomy. Everything else is already a uuid and passes through.
export const hasStagedOptions = (field) =>
  !isNil(field) && isNil(field.taxonomy_uuid) && !isEmpty(field.new_taxonomy_options)

export const UNAVAILABLE_OPTION = 'an option that no longer exists'

// An operand is a category uuid for a selection source and a literal for
// everything else. A uuid we can't name means the option was deleted out from
// under a saved gate — the service rejects the next save of that flow — so it
// says so rather than printing a uuid nobody can read. An index that hasn't
// loaded yet isn't evidence of anything, so it falls back to the raw value.
const readOperand = (value, optionNames, sourceField) => {
  const typeKey = getRowTypeKey(sourceField)
  // 'true'/'false' is how a Yes/No gate is stored, not how it should read.
  if (typeKey === BOOLEAN_TYPE_KEY) {
    return BOOLEAN_OPERAND_OPTIONS.find((option) => option.value === String(value))?.label ?? value
  }
  const known = optionNames[value]
  if (!isNil(known)) {
    return known
  }
  const isSelection = typeKey === PICK_LIST_TYPE_KEY && !hasStagedOptions(sourceField)
  return isSelection && !isEmpty(optionNames) ? UNAVAILABLE_OPTION : value
}

// Operands a selection gate references that the org's options no longer
// contain. Each one makes the flow unsavable until the gate is corrected.
export const findStaleOperands = (link, sourceField, optionNames = {}) => {
  if (isEmpty(optionNames) || getRowTypeKey(sourceField) !== PICK_LIST_TYPE_KEY) {
    return []
  }
  if (hasStagedOptions(sourceField)) {
    return []
  }
  if (!operatorNeedsValue(link.operator) || isNil(link.operator)) {
    return []
  }
  const operands =
    link.operator === EDGE_OPERATOR.in ? parseOperandList(link.compare_value) : [link.compare_value]
  return operands.filter((operand) => isNil(optionNames[operand]))
}

// Fan-in is AND, so a second gate identical to one already on the field admits
// exactly the same calls — it changes nothing, duplicates an edge on the wire,
// and gives two chips the same identity. Adding one is treated as a no-op.
export const addLink = (links, link) => {
  const isSame = (candidate) =>
    normalizeFieldKey(candidate.source) === normalizeFieldKey(link.source) &&
    normalizeFieldKey(candidate.target) === normalizeFieldKey(link.target) &&
    candidate.operator === link.operator &&
    (candidate.compare_value ?? null) === (link.compare_value ?? null)
  return links.some(isSame) ? links : [...links, link]
}

// Whether a condition reads this option, and so whether deleting it would
// orphan a gate. An operand is a category uuid once the taxonomy is saved and
// the option's NAME before that, so both have to be checked — a lock that only
// knew about uuids let an unsaved option be deleted out from under a gate.
export const isOptionGated = (option, gatedOperands = []) =>
  gatedOperands.includes(option?.taxonomy_uuid) || gatedOperands.includes(option?.name)

// Options a field has applied for testing but not saved yet, as picker choices.
// An option that already exists in the taxonomy keeps its category uuid; one
// added in the same edit has none yet, so a gate on it holds the option NAME and
// the save swaps in the uuid the taxonomy save returns — the same trick a
// brand-new field's staged options use (see resolveStagedOperands).
//
// An edit that removed every option is still the field's list: the result is
// empty, not a reason to fall back to the saved options.
export const getPendingOptionChoices = (field, pendingTaxonomies = {}) => {
  const edit = pendingTaxonomies[field?.taxonomy_uuid]
  if (isNil(edit)) {
    return null
  }
  return (edit.options ?? []).map((option) => ({
    value: option.taxonomy_uuid ?? option.name,
    label: option.name,
  }))
}

// Pending option names, indexed to themselves so a chip reading an operand that
// is a name rather than a uuid prints the name instead of calling it missing.
export const buildPendingOptionNames = (pendingTaxonomies = {}) => {
  const index = {}
  Object.values(pendingTaxonomies).forEach((edit) => {
    ;(edit.options ?? []).forEach((option) => {
      if (isNil(option.taxonomy_uuid)) {
        index[option.name] = option.name
      }
    })
  })
  return index
}

// Chip labels from pending edits: a new option by its name, as above, and an
// existing one by its uuid so a rename applied for testing shows its new name.
// Kept apart from buildPendingOptionNames, whose keys are exactly the options
// that still need saving (see findUnsavedOptionGates).
export const buildPendingOptionLabels = (pendingTaxonomies = {}) => {
  const index = {}
  Object.values(pendingTaxonomies).forEach((edit) => {
    ;(edit.options ?? []).forEach((option) => {
      index[option.taxonomy_uuid ?? option.name] = option.name
    })
  })
  return index
}

// The gates that read an option which exists only in an unsaved edit. Those
// edges can't be sent until the taxonomy is saved and the option has a uuid, so
// saving the prompt on its own has to be refused while any of these stand.
export const findUnsavedOptionGates = (links = [], pendingTaxonomies = {}) => {
  const pendingNames = new Set(Object.keys(buildPendingOptionNames(pendingTaxonomies)))
  if (isEmpty(pendingNames)) {
    return []
  }
  return links.filter((link) => {
    if (!operatorNeedsValue(link.operator) || isNil(link.operator)) {
      return false
    }
    const operands =
      link.operator === EDGE_OPERATOR.in
        ? parseOperandList(link.compare_value)
        : [link.compare_value]
    return operands.some((operand) => pendingNames.has(operand))
  })
}

// Taxonomy saves come back keyed by taxonomy uuid; the operand resolution is
// keyed by field name. Bridge the two so a saved pending edit can hand its new
// option uuids to the edges that reference them by name.
export const buildOptionUuidsByFieldKey = (promptFields = [], savedTaxonomies = {}) => {
  const byFieldKey = {}
  promptFields.forEach((field) => {
    const saved = savedTaxonomies[field.taxonomy_uuid]
    if (isNil(saved)) {
      return
    }
    const byName = {}
    ;(saved.options ?? []).forEach((option) => {
      byName[option.name] = option.taxonomy_uuid
    })
    byFieldKey[field.key] = byName
  })
  return byFieldKey
}

// Longest `in` operand list spelled out on a chip before it becomes "N values".
const SPELLED_OPERAND_BUDGET = 28

// A condition chip takes the type colour of the field it reads from, matching
// that field's type pill and the rail line feeding it. Pick List has no Pill
// colour variant (the type pill reaches for the raw cyan scale for the same
// reason), so it carries a modifier class instead.
const CHIP_VARIANT_BY_TYPE = {
  string: { brand: true },
  integer: { caution: true },
  boolean: { success: true },
  [PICK_LIST_TYPE_KEY]: {},
}

export const getConditionChipVariant = (sourceField) => {
  const typeKey = isNil(sourceField) ? null : getRowTypeKey(sourceField)
  return {
    pillProps: CHIP_VARIANT_BY_TYPE[typeKey] ?? CHIP_VARIANT_BY_TYPE.string,
    isPickList: typeKey === PICK_LIST_TYPE_KEY,
  }
}

// The chip's wording: "Call Reason is Billing". A long `in` list collapses to a
// count so the strip stays one line; the full list rides the tooltip.
export const describeLink = (link, sourceField, optionNames = {}) => {
  const name = sourceField?.key ?? link.source
  const operator = getOperatorLabel(sourceField, link.operator)
  if (!operatorNeedsValue(link.operator)) {
    return `${name} ${operator}`
  }
  if (link.operator === EDGE_OPERATOR.in) {
    const values = parseOperandList(link.compare_value).map((item) =>
      readOperand(item, optionNames, sourceField)
    )
    const spelled = values.join(', ')
    // Two long option names overflow the chip as readily as five short ones,
    // so the count stands in on length, not just on how many there are. The
    // full list is always on the chip's tooltip.
    return values.length <= 2 && spelled.length <= SPELLED_OPERAND_BUDGET
      ? `${name} ${operator} ${spelled}`
      : `${name} ${operator} ${values.length} values`
  }
  return `${name} ${operator} ${readOperand(link.compare_value, optionNames, sourceField)}`
}

export const describeLinkFully = (link, sourceField, optionNames = {}) => {
  const name = sourceField?.key ?? link.source
  const operator = getOperatorLabel(sourceField, link.operator)
  if (!operatorNeedsValue(link.operator)) {
    return `${name} ${operator}`
  }
  if (link.operator === EDGE_OPERATOR.in) {
    const values = parseOperandList(link.compare_value).map((item) =>
      readOperand(item, optionNames, sourceField)
    )
    return `${name} ${operator} ${values.join(', ')}`
  }
  return `${name} ${operator} ${readOperand(link.compare_value, optionNames, sourceField)}`
}

// Renaming a field moves both ends of every link that touched it, so a rename
// can't orphan an edge.
export const renameFieldInLinks = (links, previousKey, nextKey) => {
  const previous = normalizeFieldKey(previousKey)
  return links.map((link) => ({
    ...link,
    source: normalizeFieldKey(link.source) === previous ? nextKey : link.source,
    target: normalizeFieldKey(link.target) === previous ? nextKey : link.target,
  }))
}

// A condition can't outlive its source, and a deleted field's own conditions go
// with it.
export const removeFieldFromLinks = (links, fieldKey) => {
  const removed = normalizeFieldKey(fieldKey)
  return links.filter(
    (link) =>
      normalizeFieldKey(link.source) !== removed && normalizeFieldKey(link.target) !== removed
  )
}

// Why this field can't move to the other visibility group, or '' when it can.
// Same rule as canDependOn, checked in both directions against the links that
// already exist.
export const getMoveBlockedReason = (promptFields, links, field, toAgentVisible) => {
  if (toAgentVisible) {
    const laterSource = getLinksForTarget(links, field.key)
      .map((link) => findPromptField(promptFields, link.source))
      .find((source) => !isNil(source) && source.show_to_user !== true)
    if (!isNil(laterSource)) {
      return `Shown to Agent fields run first, so this can't depend on ${laterSource.key} (Processed Later).`
    }
    return ''
  }

  const agentDependent = getLinksForSource(links, field.key)
    .map((link) => findPromptField(promptFields, link.target))
    .find((target) => !isNil(target) && target.show_to_user === true)
  if (!isNil(agentDependent)) {
    return `${agentDependent.key} is shown to the agent and depends on this field, so it can't move later.`
  }
  return ''
}

// Links the editor can actually represent: both endpoints are rows in this
// configuration. A seeded edge pointing outside the flow is dropped rather than
// rendered as a chip nobody can resolve or edit.
export const seedFieldLinks = (edges, promptFields) =>
  edges
    .filter(
      (edge) =>
        !isNil(findPromptField(promptFields, edge.source_field_name)) &&
        !isNil(findPromptField(promptFields, edge.target_field_name))
    )
    .map((edge) => ({
      source: edge.source_field_name,
      target: edge.target_field_name,
      operator: edge.operator,
      compare_value: edge.compare_value ?? null,
    }))
    // Folded through the same no-duplicates rule the editor applies, so a flow
    // built by a script can't seed two identical gates. With both entry points
    // deduped a gate's (source, target, operator, value) is a real identity —
    // which is what the chips key off.
    .reduce(addLink, [])

// Swap authored option names for the category uuids a save just minted.
// `optionUuidsByFieldKey` is { fieldName: { optionName: categoryUuid } }.
export const resolveStagedOperands = (links, promptFields, optionUuidsByFieldKey) => {
  if (isEmpty(optionUuidsByFieldKey)) {
    return links
  }
  return links.map((link) => {
    const minted = optionUuidsByFieldKey[link.source]
    if (isNil(minted) || !operatorNeedsValue(link.operator) || isNil(link.operator)) {
      return link
    }
    if (link.operator === EDGE_OPERATOR.in) {
      const resolved = parseOperandList(link.compare_value).map((name) => minted[name] ?? name)
      return { ...link, compare_value: serializeOperandList(resolved) }
    }
    return { ...link, compare_value: minted[link.compare_value] ?? link.compare_value }
  })
}

// The operands every gate reads from one field, so its options editor can show
// which options are load-bearing. A saved option is referenced by category uuid
// and a staged one by name; either way it's whatever the gate stored.
export const getGatedOperands = (links, fieldKey) => {
  const operands = []
  getLinksForSource(links, fieldKey).forEach((link) => {
    if (isNil(link.operator) || !operatorNeedsValue(link.operator)) {
      return
    }
    const values =
      link.operator === EDGE_OPERATOR.in
        ? parseOperandList(link.compare_value)
        : [link.compare_value]
    values.forEach((value) => {
      if (!isNil(value) && !operands.includes(value)) {
        operands.push(value)
      }
    })
  })
  return operands
}

// The `edges` array POST /bit-flows takes. An unconditional edge (no operator)
// carries no compare_value, and neither does a presence test.
export const buildEdgePayload = (links) =>
  links.map((link) => ({
    source_field_name: link.source,
    target_field_name: link.target,
    operator: link.operator ?? null,
    compare_value:
      !isNil(link.operator) && operatorNeedsValue(link.operator) ? link.compare_value : null,
  }))

// A stable, order-insensitive rendering of an edge set. The API returns edges
// in its own order, so comparing them as authored would report a change that
// isn't one.
const edgeFingerprint = (links) =>
  buildEdgePayload(links)
    .map((edge) =>
      [edge.source_field_name, edge.target_field_name, edge.operator, edge.compare_value].join(
        '\u0000'
      )
    )
    .sort()
    .join('\u0001')

// Whether the flow's links differ from a saved set — drives both "Restore
// Fields" and whether a save has anything to send.
export const haveLinksChanged = (links, snapshot) =>
  edgeFingerprint(links) !== edgeFingerprint(snapshot)
